import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSModalDialog,
  MDSSelect,
  MDSText,
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextVariant,
} from "@/mds";
import { formatStatusLabel } from "@/pages/agent/utils/agent-status.utils";
import type { AssistantProjectObservable, AssistantTaskObservable } from "@/store/assistant";
import { AssistantTaskStatus } from "@/store/assistant/types";

export type AgentTaskEditorValue = {
  title?: string;
  description?: string | null;
  status?: AssistantTaskStatus;
  dueAt?: string | null;
  projectId?: string | null;
};

type AgentTaskEditorFormValue = {
  title: string;
  description: string;
  status: AssistantTaskStatus;
  dueAt: string;
  projectId: string;
};

export const AgentTaskEditor = ({
  task,
  projects,
  isOpen,
  isSaving,
  error,
  onClose,
  onSave,
}: {
  task: AssistantTaskObservable;
  projects: AssistantProjectObservable[];
  isOpen: boolean;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (value: AgentTaskEditorValue) => void;
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const initialValueRef = useRef<AgentTaskEditorFormValue | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<AssistantTaskStatus>(AssistantTaskStatus.Pending);
  const [dueAt, setDueAt] = useState("");
  const [projectId, setProjectId] = useState(NO_PROJECT_VALUE);

  useEffect(() => {
    const initialValue = getTaskEditorFormValue({ task });

    initialValueRef.current = initialValue;
    setTitle(initialValue.title);
    setDescription(initialValue.description);
    setStatus(initialValue.status);
    setDueAt(initialValue.dueAt);
    setProjectId(initialValue.projectId);
  }, [isOpen, task]);

  const statusItems = EDITABLE_TASK_STATUSES.map(statusValue => ({
    id: statusValue,
    kind: MDSMenuContentItemKind.Item,
    label: formatStatusLabel({ status: statusValue }),
  }));
  const projectItems = [
    {
      id: NO_PROJECT_VALUE,
      kind: MDSMenuContentItemKind.Item,
      label: "No project",
      icon: MDSIconKind.Folder,
    },
    ...projects
      .slice()
      .sort((left, right) => left.title.localeCompare(right.title))
      .map(project => ({
        id: project.id,
        kind: MDSMenuContentItemKind.Item,
        label: project.title || "Untitled project",
        icon: MDSIconKind.Folder,
      })),
  ];
  const formValue = { title, description, status, dueAt, projectId };
  const taskChanges = buildTaskEditorChanges({
    formValue,
    initialValue: initialValueRef.current,
  });
  const hasChanges = Object.keys(taskChanges).length > 0;
  const submit = (event: FormEvent) => {
    event.preventDefault();

    const changes = buildTaskEditorChanges({
      formValue,
      initialValue: initialValueRef.current,
    });

    if (Object.keys(changes).length === 0) return;

    onSave(changes);
  };

  return (
    <MDSModalDialog isOpen={isOpen} handleClose={onClose}>
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        Edit task
      </MDSText>
      <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
        <StyledForm ref={formRef} onSubmit={submit}>
          <StyledLabel>
            Title
            <MDSTextField
              aria-label="Task title"
              autoFocus
              value={title}
              variant={MDSTextFieldVariant.Form}
              onChange={setTitle}
            />
          </StyledLabel>
          <StyledLabel>
            Description
            <MDSTextField
              aria-label="Task description"
              isTextArea
              textAreaProps={{ rows: 5 }}
              value={description}
              variant={MDSTextFieldVariant.Form}
              onChange={setDescription}
            />
          </StyledLabel>
          <StyledTwoColumnFields>
            <StyledLabel>
              Status
              <StyledSelect
                label="Task status"
                items={statusItems}
                selectedKey={status}
                buttonVariant={MDSButtonVariant.Chrome}
                onSelectionChange={key => {
                  if (typeof key === "string") {
                    setStatus(normalizeEditableStatus({ status: key }));
                  }
                }}
              />
            </StyledLabel>
            <StyledLabel>
              Project
              <StyledSelect
                label="Task project"
                items={projectItems}
                selectedKey={projectId}
                buttonVariant={MDSButtonVariant.Chrome}
                onSelectionChange={key => {
                  if (typeof key === "string") {
                    setProjectId(key);
                  }
                }}
              />
            </StyledLabel>
          </StyledTwoColumnFields>
          <StyledLabel>
            Due date
            <StyledDateInput
              aria-label="Task due date"
              type="datetime-local"
              value={dueAt}
              onChange={event => setDueAt(event.currentTarget.value)}
            />
          </StyledLabel>
          {error ? <StyledError role="alert">{error}</StyledError> : null}
        </StyledForm>
      </MDSDialogTextContent>
      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton variant={MDSButtonVariant.Transparent} onPress={onClose}>
          Cancel
        </MDSButton>
        <MDSButton
          isDisabled={isSaving || !title.trim() || !hasChanges}
          variant={MDSButtonVariant.Contrast}
          onPress={() => formRef.current?.requestSubmit()}
        >
          {isSaving ? "Saving…" : "Save changes"}
        </MDSButton>
      </MDSButtonGroup>
    </MDSModalDialog>
  );
};

const NO_PROJECT_VALUE = "no-project";
const EDITABLE_TASK_STATUSES = [
  AssistantTaskStatus.Pending,
  AssistantTaskStatus.InProgress,
  AssistantTaskStatus.Completed,
  AssistantTaskStatus.Canceled,
] as const;

const normalizeEditableStatus = ({ status }: { status: string }): AssistantTaskStatus => {
  const editableStatus = EDITABLE_TASK_STATUSES.find(value => value === status);
  return editableStatus ?? AssistantTaskStatus.Pending;
};

const getTaskEditorFormValue = ({
  task,
}: {
  task: AssistantTaskObservable;
}): AgentTaskEditorFormValue => ({
  title: task.title,
  description: task.description ?? "",
  status: normalizeEditableStatus({ status: task.status }),
  dueAt: toDateTimeLocalValue({ value: task.dueAt }),
  projectId: task.projectId ?? NO_PROJECT_VALUE,
});

const buildTaskEditorChanges = ({
  formValue,
  initialValue,
}: {
  formValue: AgentTaskEditorFormValue;
  initialValue: AgentTaskEditorFormValue | null;
}): AgentTaskEditorValue => {
  if (!initialValue) return {};

  const changes: AgentTaskEditorValue = {};

  if (formValue.title !== initialValue.title) {
    changes.title = formValue.title.trim();
  }

  if (formValue.description !== initialValue.description) {
    changes.description = formValue.description.trim() || null;
  }

  if (formValue.status !== initialValue.status) {
    changes.status = formValue.status;
  }

  if (formValue.dueAt !== initialValue.dueAt) {
    changes.dueAt = fromDateTimeLocalValue({ value: formValue.dueAt });
  }

  if (formValue.projectId !== initialValue.projectId) {
    changes.projectId = formValue.projectId === NO_PROJECT_VALUE ? null : formValue.projectId;
  }

  return changes;
};

const toDateTimeLocalValue = ({ value }: { value: string | null }) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return localDate.toISOString().slice(0, 16);
};

const fromDateTimeLocalValue = ({ value }: { value: string }) => {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const StyledLabel = styled.label`
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.ParagraphS]};
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StyledTwoColumnFields = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StyledSelect = styled(MDSSelect)`
  min-width: 0;
`;

const StyledDateInput = styled.input`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  min-height: 40px;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};

  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.border.emphasis};
    outline: none;
  }
`;

const StyledError = styled.div`
  color: ${({ theme }) => theme.colors.text.danger};
`;
